import { motion } from 'framer-motion';
import { Plus } from 'lucide-react';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import AverageMarksChart from '../components/students/AverageMarksChart';
import DefaultersTable from '../components/students/DefaultersTable';
import { LoadingSpinner } from '../components/ui/loading-spinner.tsx';
import { useGetChildren } from '../api/student/studentQueries';

export default function Students() {
  const { data: students, isLoading, error } = useGetChildren();
  const navigate = useNavigate();

  // Calculate stats from real data
  const stats = React.useMemo(() => {
    if (!students || !Array.isArray(students))
      return {
        totalStudents: { count: 0, percentageChange: 0 },
      };

    const totalStudents = students.length;

    return {
      totalStudents: {
        count: totalStudents,
        percentageChange: 0, // Base value for comparison
      },
    };
  }, [students]);

  const renderContent = () => {
    if (error) {
      return <div className="text-red-500">Error: {error.message}</div>;
    }

    if (isLoading) {
      return (
        <div className="flex items-center justify-center h-[calc(100vh-5rem)]">
          <div className="text-center">
            <LoadingSpinner size="lg" className="mb-4" />
            <p className="text-muted-foreground">Loading students data...</p>
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-8">
        <div className="md:grid md:grid-cols-3 md:gap-6 flex flex-col gap-y-4 ">
          <div className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-600">
                    Total Students
                  </p>
                  <h3 className="mt-2 text-3xl font-bold text-gray-900">
                    {stats.totalStudents.count.toLocaleString()}
                  </h3>
                </div>
                <div className="p-3 bg-purple-100 rounded-full">
                  <svg
                    className="w-8 h-8 text-purple-600"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"
                    />
                  </svg>
                </div>
              </div>
              <div className="mt-4 flex items-center justify-between">
                <button
                  onClick={() => navigate('/student/list')}
                  className="text-sm text-purple-600 hover:text-purple-700 font-medium flex items-center"
                >
                  View all students
                  <svg
                    className="ml-2 w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 5l7 7-7 7"
                    />
                  </svg>
                </button>
                {stats.totalStudents.percentageChange !== 0 && (
                  <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                    +{stats.totalStudents.percentageChange}%
                  </span>
                )}
              </div>
            </div>
          </div>

          <div className="md:col-start-3 flex md:items-center justify-center md:justify-end w-full">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className={`min-w-[160px] h-[52px] inline-flex items-center justify-center px-6 border border-purple-200 rounded-lg shadow-sm text-base font-medium ${
                isLoading
                  ? 'bg-purple-100 text-purple-400 cursor-not-allowed'
                  : 'bg-purple-50 text-purple-700 hover:bg-purple-100 hover:border-purple-300'
              } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500 transition-all duration-200`}
              onClick={() => navigate('/student/add')}
              disabled={isLoading}
            >
              <Plus className="h-5 w-5 mr-2" />
              Add Student
            </motion.button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Average Marks
            </h2>
            <AverageMarksChart
              data={students?.averageMarks}
              loading={isLoading}
            />
          </div>
          <div className="bg-white rounded-lg shadow-sm p-6">
            <h2 className="text-lg font-medium text-gray-900 mb-4">
              Defaulters
            </h2>
            <DefaultersTable data={students?.defaulters} loading={isLoading} />
          </div>
        </div>
      </div>
    );
  };

  return <main className="flex-1 overflow-y-auto p-6">{renderContent()}</main>;
}
